import { PDFDocument, StandardFonts } from 'pdf-lib';
import { searchCarriers } from '../../api/figApi';
import { shouldPopulateTargetCarrier } from './acordTransactionStatus';
import { formatIsoToAcord, normalizeToIsoDate } from '../../utils/dateNormalize';

const BLANK_PDF_URL = '/acord/ACORD_126_blank.pdf';

/**
 * Generate a filled ACORD 126 — Commercial General Liability Section.
 */
export default async function generateACORD126(formData, insured, getToken, transactionStatus) {
  const res = await fetch(BLANK_PDF_URL);
  if (!res.ok) throw new Error(`Failed to load blank ACORD 126 (${res.status})`);
  const pdfBytes = await res.arrayBuffer();

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  function setText(name, value) {
    if (!value && value !== 0) return;
    try {
      const field = form.getTextField(name);
      field.setText(String(value));
      try { field.updateAppearances(font); } catch {}
    } catch {}
  }

  function setCheck(name) {
    try { form.getCheckBox(name).check(); } catch {}
  }

  const f = formData;
  const fmtNum = (v) => v ? Number(v).toLocaleString() : '';
  const today = new Date().toISOString().split('T')[0];

  // Populate the top-of-form CARRIER / NAIC / POLICY # only on Renew.
  // 126 is CGL-only by design; pass a size-1 Set so the helper's multi-LOB
  // guard is satisfied without introducing a side door.
  const showCarrier = shouldPopulateTargetCarrier(
    transactionStatus,
    new Set(['Commercial General Liability']),
  );

  // ============ PAGE 1 — Header ============
  setText('Form_CompletionDate_A', f.date || today);
  setText('Producer_FullName_A', 'Fragos Insurance Group LLC');
  // Agency Customer ID — parent field has multiple widgets; one write propagates.
  if (insured?.id) setText('Producer_CustomerIdentifier_A', `FIG-${insured.id}`);
  // State is ISO; ACORD convention is MM/DD/YYYY.
  setText('Policy_EffectiveDate_A', formatIsoToAcord(normalizeToIsoDate(f.effectiveDate)));
  setText('NamedInsured_FullName_A', insured?.CompanyName || '');

  if (showCarrier) {
    setText('Policy_PolicyNumberIdentifier_A', f.policyNumber);
    setText('Insurer_FullName_A', f.carrier);
    setText('Insurer_NAICCode_A', f.naicCode);

    // Carrier address lookup
    if (getToken && f.naicCode) {
      try {
        const token = await getToken();
        const results = await searchCarriers(token, f.carrier || '');
        const match = results.find(c => String(c.NAICCode) === String(f.naicCode));
        if (match) {
          setText('Insurer_MailingAddress_AddressLineOne_A', match.Address);
          setText('Insurer_MailingAddress_CityName_A', match.City);
          setText('Insurer_MailingAddress_StateOrProvinceCode_A', match.State);
          setText('Insurer_MailingAddress_PostalCode_A', match.Zip);
        }
      } catch {}
    }
  }

  // Coverage Type checkboxes
  setCheck('GeneralLiability_CoverageIndicator_A');
  if (f.coverageForm === 'Occurrence') setCheck('GeneralLiability_OccurrenceIndicator_A');
  if (f.coverageForm === 'Claims Made') setCheck('GeneralLiability_ClaimsMadeIndicator_A');
  if (f.coverageForm === 'O&CP') setCheck('GeneralLiability_OwnersAndContractorsProtectiveIndicator_A');
  setText('GeneralLiability_OtherCoverageDescription_A', f.otherCoverage);

  // Limits
  setText('GeneralLiability_EachOccurrence_LimitAmount_A', fmtNum(f.eachOccurrence));
  setText('GeneralLiability_FireDamageRentedPremises_EachOccurrenceLimitAmount_A', fmtNum(f.fireDamage));
  setText('GeneralLiability_MedicalExpense_EachPersonLimitAmount_A', fmtNum(f.medExpense));
  setText('GeneralLiability_PersonalAndAdvertisingInjury_LimitAmount_A', fmtNum(f.personalAdvInjury));
  setText('GeneralLiability_GeneralAggregate_LimitAmount_A', fmtNum(f.generalAggregate));
  setText('GeneralLiability_ProductsAndCompletedOperations_AggregateLimitAmount_A', fmtNum(f.productsCompOps));
  setText('GeneralLiability_EmployeeBenefits_LimitAmount_A', fmtNum(f.employeeBenefits));
  setText('GeneralLiability_OtherCoverageLimitDescription_A', f.otherLimitDesc);
  setText('GeneralLiability_OtherCoverageLimitAmount_A', fmtNum(f.otherLimitAmt));

  // Aggregate applies per
  if (f.aggregateAppliesPer === 'Policy') setCheck('GeneralLiability_GeneralAggregate_LimitAppliesPerPolicyIndicator_A');
  if (f.aggregateAppliesPer === 'Project') setCheck('GeneralLiability_GeneralAggregate_LimitAppliesPerProjectIndicator_A');
  if (f.aggregateAppliesPer === 'Location') setCheck('GeneralLiability_GeneralAggregate_LimitAppliesPerLocationIndicator_A');

  // Deductibles
  if (f.pdDeductible) {
    setCheck('GeneralLiability_PropertyDamage_DeductibleIndicator_A');
    setText('GeneralLiability_PropertyDamage_DeductibleAmount_A', fmtNum(f.pdDeductible));
  }
  if (f.biDeductible) {
    setCheck('GeneralLiability_BodilyInjury_DeductibleIndicator_A');
    setText('GeneralLiability_BodilyInjury_DeductibleAmount_A', fmtNum(f.biDeductible));
  }
  if (f.deductibleBasis === 'Per Claim') setCheck('GeneralLiability_DeductiblePerClaimIndicator_A');
  if (f.deductibleBasis === 'Per Occurrence') setCheck('GeneralLiability_DeductiblePerOccurrenceIndicator_A');

  // Premiums
  setText('GeneralLiability_PremisesOperations_PremiumAmount_A', fmtNum(f.premOpsPremium));
  setText('GeneralLiability_Products_PremiumAmount_A', fmtNum(f.productsPremium));
  setText('GeneralLiability_OtherCoveragePremiumAmount_A', fmtNum(f.otherPremium));
  setText('GeneralLiabilityLineOfBusiness_TotalPremiumAmount_A', fmtNum(f.totalPremium));
  setText('GeneralLiabilityLineOfBusiness_RemarkText_A', f.remarks);

  // UM/UIM and Medical Payments
  if (f.umAvailable === 'Y') setCheck('GeneralLiability_UninsuredUnderinsuredMotorists_CoverageAvailableIndicator_A');
  if (f.umAvailable === 'N') setCheck('GeneralLiability_UninsuredUnderinsuredMotorists_CoverageAvailableNoIndicator_A');
  if (f.medPayAvailable === 'Y') setCheck('GeneralLiability_MedicalPayments_CoverageAvailableIndicator_A');
  if (f.medPayAvailable === 'N') setCheck('GeneralLiability_MedicalPayments_CoverageAvailableNoIndicator_A');

  // Schedule of Hazards (3 rows: _A, _B, _C)
  // Intake form pre-assigns haz# "1","2","3" for all 3 slots — skip the
  // whole row (including the pre-assigned number) when no real data exists.
  const hazSuffixes = ['_A', '_B', '_C'];
  if (f.haz) {
    f.haz.forEach((h, i) => {
      if (i > 2) return;
      const hasData = !!(h.classCode || h.classification || h.exposure);
      if (!hasData) return;
      const s = hazSuffixes[i];
      setText(`GeneralLiability_Hazard_LocationProducerIdentifier${s}`, h.loc);
      setText(`GeneralLiability_Hazard_HazardProducerIdentifier${s}`, h.haz);
      setText(`GeneralLiability_Hazard_ClassCode${s}`, h.classCode);
      setText(`GeneralLiability_Hazard_Classification${s}`, h.classification);
      setText(`GeneralLiability_Hazard_PremiumBasisCode${s}`, h.premBasis);
      setText(`GeneralLiability_Hazard_Exposure${s}`, h.exposure);
      setText(`GeneralLiability_Hazard_TerritoryCode${s}`, h.territory);
      setText(`GeneralLiability_Hazard_PremisesOperationsRate${s}`, h.poRate);
      setText(`GeneralLiability_Hazard_ProductsRate${s}`, h.prodRate);
      setText(`GeneralLiability_Hazard_PremisesOperationsPremiumAmount${s}`, fmtNum(h.poPremium));
      setText(`GeneralLiability_Hazard_ProductsPremiumAmount${s}`, fmtNum(h.prodPremium));
    });
  }

  // Claims Made
  setText('GeneralLiability_ClaimsMade_ProposedRetroactiveDate_A', f.retroDate);
  setText('GeneralLiability_ClaimsMade_UninterruptedCoverageEntryDate_A', f.entryDate);

  // Employee Benefits
  setText('GeneralLiability_EmployeeBenefits_PerClaimDeductibleAmount_A', fmtNum(f.ebDeductible));
  setText('GeneralLiability_EmployeeBenefits_EmployeeCount_A', f.ebEmployees);
  setText('GeneralLiability_EmployeeBenefits_EmployeeCoveredCount_A', f.ebCovered);
  setText('GeneralLiability_EmployeeBenefits_RetroactiveDate_A', f.ebRetroDate);

  // ============ PAGE 2 — Contractors ============
  setText('Contractors_FullTimeEmployeeCount_A', f.contractorsFT);
  setText('Contractors_PartTimeEmployeeCount_A', f.contractorsPT);
  setText('Contractors_WorkSubcontractedPercent_A', f.subcontractedPct);
  setText('Contractors_SubcontractorsPaidAmount_A', fmtNum(f.subcontractorsPaid));
  setText('GeneralLiabilityLineOfBusiness_TypeOfWorkSubcontractedDescription_A', f.workSubcontracted);

  // Contractors questions
  const cqFields = [
    ['Contractors_Question_AABCode_A', 'GeneralLiabilityLineOfBusiness_ApplicantDrawPlansForOthersExplanation_A'],
    ['Contractors_Question_AAICode_A', 'GeneralLiabilityLineOfBusiness_OperationsIncludeBlastingExplanation_A'],
    ['Contractors_Question_AACCode_A', 'GeneralLiabilityLineOfBusiness_OperationsIncludeEvacuationExplanation_A'],
    ['Contractors_Question_ABBCode_A', 'GeneralLiabilityLineOfBusiness_SubcontractorsCarryLowerLimitsExplanation_A'],
    ['Contractors_Question_AADCode_A', 'GeneralLiabilityLineOfBusiness_SubcontractorsWorksWithoutCertificatesOfInsuranceExplanation_A'],
    ['Contractors_Question_AAECode_A', 'GeneralLiabilityLineOfBusiness_ApplicantLeaseEquipmentToOthersExplanation_A'],
  ];
  if (f.cq) {
    f.cq.forEach((q, i) => {
      if (cqFields[i]) {
        setText(cqFields[i][0], (q.answer || '').trim().charAt(0).toUpperCase());
        setText(cqFields[i][1], q.exp);
      }
    });
  }

  // Products (3 rows: _A, _B, _C)
  const prodSuffixes = ['_A', '_B', '_C'];
  if (f.products) {
    f.products.forEach((prod, i) => {
      if (i > 2) return;
      const s = prodSuffixes[i];
      setText(`ProductAndCompletedOperations_ProductName${s}`, prod.name);
      setText(`ProductAndCompletedOperations_AnnualGrossSalesAmount${s}`, fmtNum(prod.grossSales));
      setText(`ProductAndCompletedOperations_UnitCount${s}`, prod.units);
      setText(`ProductAndCompletedOperations_InMarketMonthCount${s}`, prod.monthsInMarket);
      setText(`ProductAndCompletedOperations_ExpectedLifeMonthCount${s}`, prod.expectedLife);
      setText(`ProductAndCompletedOperations_IntendedUse${s}`, prod.intendedUse);
      setText(`ProductAndCompletedOperations_PrincipalComponents${s}`, prod.components);
    });
  }

  // Products questions
  const pqFields = [
    ['GeneralLiabilityLineOfBusiness_Question_AAJCode_A', 'GeneralLiabilityLineOfBusiness_ApplicantInstallServiceProductsExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABACode_A', null],
    ['GeneralLiabilityLineOfBusiness_Question_ABBCode_A', 'GeneralLiabilityLineOfBusiness_ResearchDevelopmentConductedNewProductsPlannedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABCCode_A', 'GeneralLiabilityLineOfBusiness_GuaranteesWarrantiesHoldHarmlessExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABDCode_A', 'GeneralLiabilityLineOfBusiness_ProductsRelatedToAircraftIndustryExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABECode_A', 'GeneralLiabilityLineOfBusiness_ProductsRecalledDiscontinuedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABFCode_A', 'GeneralLiabilityLineOfBusiness_ProductsOfOthersSoldUnderApplicantLabelExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABGCode_A', 'GeneralLiabilityLineOfBusiness_ProductsUnderLabelOfOthersExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABHCode_A', 'GeneralLiabilityLineOfBusiness_VendorCoverageRequiredExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ABICode_A', 'GeneralLiabilityLineOfBusiness_NamedInsuredSellToOtherNamedInsuredExplanation_R'],
  ];
  if (f.pq) {
    f.pq.forEach((q, i) => {
      if (pqFields[i]) {
        setText(pqFields[i][0], (q.answer || '').trim().charAt(0).toUpperCase());
        if (pqFields[i][1]) setText(pqFields[i][1], q.exp);
      }
    });
  }

  // ============ PAGE 3 — Additional Interest ============
  setText('AdditionalInterest_FullName_A', f.interestName);
  setText('AdditionalInterest_MailingAddress_LineOne_A', f.interestAddress);
  setText('AdditionalInterest_MailingAddress_CityName_A', f.interestCity);
  setText('AdditionalInterest_MailingAddress_StateOrProvinceCode_A', f.interestState);
  setText('AdditionalInterest_MailingAddress_PostalCode_A', f.interestZip);

  // General info questions (page 3)
  const gqFields = [
    ['GeneralLiabilityLineOfBusiness_Question_ABJCode_A', 'GeneralLiabilityLineOfBusiness_AnyMedicalFacilitiesProvidedMedicalProfessionalsEmployedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACACode_A', 'GeneralLiabilityLineOfBusiness_AnyExposureRadioactiveMaterialsExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACJCode_A', 'GeneralLiabilityLineOfBusiness_OperationsInvolveStoringDisposingTransportingHazardousMaterialExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACBCode_A', 'GeneralLiabilityLineOfBusiness_AnyListedOperationsSoldAcquiredLastFiveYearsExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACCCode_A', 'GeneralLiabilityLineOfBusiness_MachineryOrEquipmentLoanedRentedOthersExplanation_A'],
    ['Contractors_Question_AAHCode_A', 'GeneralLiabilityLineOfBusiness_WatercraftDocksFloatsOwnedHiredLeasedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACDCode_A', 'GeneralLiabilityLineOfBusiness_AnyParkingFacilitiesOwnedRentedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_KAGCode_A', 'GeneralLiabilityLineOfBusiness_FeeChargedForParkingExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACECode_A', 'GeneralLiabilityLineOfBusiness_RecreationalFacilitiesProvidedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_KAACode_A', null],
    ['GeneralLiabilityLineOfBusiness_Question_KAHCode_A', null],
    ['GeneralLiabilityLineOfBusiness_Question_ACFCode_A', 'GeneralLiabilityLineOfBusiness_SportingOrSocialEventsSponsoredExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_KABCode_A', null],
    ['GeneralLiabilityLineOfBusiness_Question_ACGCode_A', 'GeneralLiabilityLineOfBusiness_StructuralAlterationsContemplatedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_ACHCode_A', 'GeneralLiabilityLineOfBusiness_DemolitionExposureContemplatedExplanation_A'],
  ];
  if (f.gq) {
    f.gq.forEach((q, i) => {
      if (gqFields[i]) {
        setText(gqFields[i][0], (q.answer || '').trim().charAt(0).toUpperCase());
        if (gqFields[i][1]) setText(gqFields[i][1], q.exp);
      }
    });
  }

  setText('BuildingOccupancy_ApartmentCount_A', f.apartmentCount);
  setText('BuildingOccupancy_ApartmentArea_A', f.apartmentArea);
  setText('GeneralLiability_Otherlodgingoperationsdescription_A', f.otherLodging);

  // ============ PAGE 4 — General Info continued ============
  const gq2Fields = [
    ['GeneralLiabilityLineOfBusiness_Question_AABCode_A', 'GeneralLiabilityLineOfBusiness_ApplicantActiveJointVenturesExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_AACCode_A', null],
    ['GeneralLiabilityLineOfBusiness_Question_AADCode_A', 'GeneralLiabilityLineOfBusiness_LabourInterchangeOtherBusinessSubsidiariesExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_AAECode_A', 'GeneralLiabilityLineOfBusiness_DayCareFacilitiesOperatedExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_AAFCode_A', 'GeneralLiabilityLineOfBusiness_CrimesOccurredOrAttemptedLastThreeYearsExplanation_A'],
    ['Contractors_Question_AAGCode_A', 'GeneralLiabilityLineOfBusiness_FormalSafetySecurityPolicyInEffectExplanation_A'],
    ['GeneralLiabilityLineOfBusiness_Question_AAGCode_A', 'GeneralLiabilityLineOfBusiness_BusinessPromotionalLiteratureSafetyRepresentationsExplanation_A'],
  ];
  if (f.gq2) {
    f.gq2.forEach((q, i) => {
      if (gq2Fields[i]) {
        setText(gq2Fields[i][0], (q.answer || '').trim().charAt(0).toUpperCase());
        if (gq2Fields[i][1]) setText(gq2Fields[i][1], q.exp);
      }
    });
  }

  // WC Carriers
  const wcNameFields = ['AdditionalInterest_FullName_B', 'AdditionalInterest_FullName_C', 'AdditionalInterest_FullName_D', 'AdditionalInterest_FullName_E'];
  const wcCodeFields = ['AdditionalInterest_WorkersCompensationCarriedCode_A', 'AdditionalInterest_WorkersCompensationCarriedCode_B', 'AdditionalInterest_WorkersCompensationCarriedCode_C', 'AdditionalInterest_WorkersCompensationCarriedCode_D'];
  if (f.wcCarriers) {
    f.wcCarriers.forEach((name, i) => {
      if (wcNameFields[i]) setText(wcNameFields[i], name);
    });
  }
  if (f.wcCodes) {
    f.wcCodes.forEach((code, i) => {
      if (wcCodeFields[i]) setText(wcCodeFields[i], code);
    });
  }

  // Remarks
  setText('GeneralLiabilityLineOfBusiness_RemarkText_B', f.remarksP4);

  // Signature — applicant fields intentionally left blank; client signs by hand.
  setText('Producer_AuthorizedRepresentative_Signature_A', 'Marc Fragos');
  setText('Producer_AuthorizedRepresentative_FullName_A', 'Marc Fragos');
  setText('Producer_StateLicenseIdentifier_A', '');
  setText('Producer_NationalIdentifier_A', '');

  // ============ Save and trigger download ============
  const filledBytes = await pdfDoc.save({ updateFieldAppearances: false });
  const blob = new Blob([filledBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ACORD_126_${(insured?.CompanyName || 'insured').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
